import React from 'react';

export interface BanInfo {
  reason: string;
  isPermanent: boolean;
  expiresAt: string | null;
  supportEmail: string;
}

interface BanMessageProps {
  banInfo: BanInfo;
  onClose?: () => void;
}

export const BanMessage: React.FC<BanMessageProps> = ({ banInfo, onClose }) => {
  const { reason, isPermanent, expiresAt, supportEmail } = banInfo;

  // Format expiration date
  const formatExpirationDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return date.toLocaleDateString('en-US', options);
  };

  // Calculate time remaining
  const getTimeRemaining = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const now = new Date();
    const expiry = new Date(dateStr);
    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return '1 day';
    if (diffDays === 7) return '7 days (1 week)';
    if (diffDays === 30) return '30 days (1 month)';
    if (diffDays > 30) return `${diffDays} days`;
    if (diffDays < 1) {
      const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
      return diffHours === 1 ? '1 hour' : `${diffHours} hours`;
    }
    return `${diffDays} days`;
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        {/* Icon */}
        <div style={styles.iconContainer}>
          <span style={styles.icon}>🚫</span>
        </div>

        {/* Title */}
        <h1 style={styles.title}>Account Suspended</h1>

        {/* Ban Type Badge */}
        <div
          style={{
            ...styles.badge,
            ...(isPermanent ? styles.badgePermanent : styles.badgeTemporary),
          }}
        >
          {isPermanent ? 'PERMANENT BAN' : 'TEMPORARY BAN'}
        </div>

        {/* Ban Details */}
        <div style={styles.detailsCard}>
          <h2 style={styles.detailsTitle}>Ban Details</h2>

          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Reason:</span>
            <span style={styles.detailValue}>{reason}</span>
          </div>

          {!isPermanent && expiresAt && (
            <>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Duration:</span>
                <span style={styles.detailValue}>{getTimeRemaining(expiresAt)}</span>
              </div>

              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Expires:</span>
                <span style={styles.detailValue}>{formatExpirationDate(expiresAt)}</span>
              </div>
            </>
          )}

          {isPermanent && (
            <div style={styles.permanentNote}>
              This ban is permanent and will not expire automatically.
            </div>
          )}
        </div>

        {/* Why This Happened */}
        <div style={styles.infoSection}>
          <h3 style={styles.infoTitle}>Why did this happen?</h3>
          <p style={styles.infoText}>
            Our moderation system or admin team determined that your account violated VibeTalk's
            Community Guidelines. This decision was made to protect other users and maintain a
            safe environment.
          </p>
        </div>

        {/* Appeal Process */}
        <div style={styles.appealSection}>
          <h3 style={styles.appealTitle}>Believe this was a mistake?</h3>
          <p style={styles.appealText}>
            You may appeal this ban by contacting our support team:
          </p>

          <div style={styles.contactInfo}>
            <div style={styles.contactRow}>
              <span style={styles.contactIcon}>📧</span>
              <a href={`mailto:${supportEmail}`} style={styles.contactLink}>
                {supportEmail}
              </a>
            </div>
            <div style={styles.contactRow}>
              <span style={styles.contactIcon}>📝</span>
              <span style={styles.contactText}>Subject: "Ban Appeal - [Your Email]"</span>
            </div>
          </div>

          <div style={styles.appealSteps}>
            <p style={styles.appealStepTitle}>Appeal Process:</p>
            <ol style={styles.appealList}>
              <li>Email our support team with your appeal details</li>
              <li>Our team will review your case within 2-3 business days</li>
              <li>You'll receive a response with the final decision</li>
            </ol>
          </div>
        </div>

        {/* Important Notes */}
        <div style={styles.warningSection}>
          <div style={styles.warningTitle}>⚠️ Important:</div>
          <ul style={styles.warningList}>
            <li>Creating new accounts to bypass this ban is strictly prohibited</li>
            <li>Multiple violations may result in permanent suspension</li>
            <li>All appeals are reviewed manually by our team</li>
          </ul>
        </div>

        {/* Close Button */}
        {onClose && (
          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '1rem',
  },
  container: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  iconContainer: {
    textAlign: 'center',
    padding: '2rem 2rem 1rem',
  },
  icon: {
    fontSize: '4rem',
    display: 'inline-block',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#dc2626',
    textAlign: 'center',
    margin: '0 0 1rem',
  },
  badge: {
    display: 'block',
    padding: '0.5rem 1.5rem',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.85rem',
    fontWeight: '700',
    letterSpacing: '0.05em',
    margin: '0 auto 2rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: 'fit-content',
  },
  badgePermanent: {
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    border: '2px solid #dc2626',
  },
  badgeTemporary: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    border: '2px solid #f59e0b',
  },
  detailsCard: {
    margin: '0 2rem 2rem',
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
    borderRadius: 'var(--radius-md)',
    border: '1px solid #e5e7eb',
  },
  detailsTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--color-dark-navy)',
    marginBottom: '1rem',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
    gap: '1rem',
  },
  detailLabel: {
    fontSize: '0.95rem',
    color: 'var(--color-gray-600)',
    fontWeight: '500',
  },
  detailValue: {
    fontSize: '0.95rem',
    color: 'var(--color-dark-navy)',
    fontWeight: '600',
    textAlign: 'right',
  },
  permanentNote: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#fef2f2',
    borderLeft: '3px solid #dc2626',
    fontSize: '0.9rem',
    color: '#991b1b',
    fontWeight: '500',
  },
  infoSection: {
    margin: '0 2rem 2rem',
  },
  infoTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--color-dark-navy)',
    marginBottom: '0.5rem',
  },
  infoText: {
    fontSize: '0.95rem',
    color: 'var(--color-gray-600)',
    lineHeight: '1.6',
    margin: 0,
  },
  appealSection: {
    margin: '0 2rem 2rem',
    padding: '1.5rem',
    backgroundColor: '#f0f9ff',
    borderRadius: 'var(--radius-md)',
    border: '1px solid #bfdbfe',
  },
  appealTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1e40af',
    marginBottom: '0.5rem',
  },
  appealText: {
    fontSize: '0.95rem',
    color: '#1e3a8a',
    marginBottom: '1rem',
  },
  contactInfo: {
    marginBottom: '1.5rem',
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.5rem',
  },
  contactIcon: {
    fontSize: '1.25rem',
  },
  contactLink: {
    color: '#2563eb',
    textDecoration: 'none',
    fontWeight: '500',
    fontSize: '0.95rem',
  },
  contactText: {
    fontSize: '0.9rem',
    color: '#1e3a8a',
  },
  appealSteps: {
    marginTop: '1rem',
  },
  appealStepTitle: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#1e40af',
    marginBottom: '0.5rem',
  },
  appealList: {
    margin: '0',
    paddingLeft: '1.5rem',
    fontSize: '0.9rem',
    color: '#1e3a8a',
    lineHeight: '1.8',
  },
  warningSection: {
    margin: '0 2rem 2rem',
    padding: '1rem 1.5rem',
    backgroundColor: '#fffbeb',
    borderRadius: 'var(--radius-md)',
    border: '1px solid #fcd34d',
  },
  warningTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#92400e',
    marginBottom: '0.75rem',
  },
  warningList: {
    margin: 0,
    paddingLeft: '1.5rem',
    fontSize: '0.9rem',
    color: '#78350f',
    lineHeight: '1.8',
  },
  closeButton: {
    display: 'block',
    margin: '0 2rem 2rem',
    padding: '0.875rem 2rem',
    width: 'calc(100% - 4rem)',
    backgroundColor: 'var(--color-gray-600)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
};
